/* eslint-disable unused-imports/no-unused-vars */
/* eslint-disable jsx-a11y/alt-text */
"use client";

import Image, { ImageProps, StaticImageData } from "next/image";

import { getOptimizedImageUrl } from "@/utils/utils";

type ImageLayoutType =
  | "full" // Full width
  | "grid-2" // Two columns
  | "grid-3" // Three columns
  | "grid-4" // Four columns
  | "sidebar" // Sidebar fixed width
  | "custom"; // Custom sizes

interface OptimizedImageProps extends Omit<ImageProps, "src" | "sizes" | "height"> {
  src: string | StaticImageData;
  imageLayout?: ImageLayoutType;
  sizes?: string;
  maxWidth?: number;
  height?: number;
  skipCloudFrontParams?: boolean;
  isBanner?: boolean;
  objectFit?: "cover" | "contain" | "fill" | "none" | "scale-down";
}

interface CloudFrontLoaderProps {
  src: string;
  width?: number;
  quality?: number;
}

const SIZES_MAP = {
  full: "100vw",
  "grid-2": "(max-width: 768px) 100vw, 50vw",
  "grid-3": "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw",
  "grid-4": "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw",
  sidebar: "(max-width: 768px) 100vw, 300px",
} as const;

const isDevelopment = process.env.NEXT_PUBLIC_DEV_MODE === "true";

const createCloudFrontLoader = (
  height?: number,
  maxWidth?: number,
  skipCloudFrontParams?: boolean,
  isBanner?: boolean,
  objectFit?: "cover" | "contain" | "fill" | "none" | "scale-down",
) => {
  return ({ src, width, quality = 75 }: CloudFrontLoaderProps) => {
    if (src.startsWith("data:") || src.startsWith("blob:") || src.startsWith("/_next/")) {
      return `${src}?w=${width}&q=${quality}`;
    }

    if (isDevelopment) {
      const finalWidth = width && maxWidth ? Math.min(width, maxWidth) : width;
      return getOptimizedImageUrl(src, {
        width: isBanner ? "auto" : finalWidth,
        quality,
        crop: objectFit === "contain" ? "limit" : "fill",
      });
    }

    try {
      const url = new URL(src);

      const finalWidth = width && maxWidth ? Math.min(width, maxWidth) : width;

      //! We don't resize banners regardless of anything, instead serve in the original size
      //! Only transform into webp
      if (finalWidth && !url.searchParams.has("width") && !skipCloudFrontParams) {
        const widthParam = isBanner ? "auto" : finalWidth.toString();

        url.searchParams.set("width", widthParam);
      }

      // Use the height from props
      if (height && !url.searchParams.has("height") && !skipCloudFrontParams) {
        !isBanner && url.searchParams.set("height", height.toString());
      }

      if (quality && !skipCloudFrontParams) {
        url.searchParams.set("quality", quality.toString());
      }

      if (!skipCloudFrontParams) {
        url.searchParams.set("format", "webp");
      }

      return url.toString();
    } catch (error) {
      console.error("Invalid URL:", src);
      return src;
    }
  };
};

const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  imageLayout = "full",
  maxWidth,
  skipCloudFrontParams = false,
  isBanner = false,
  objectFit = "cover",
  ...props
}) => {
  const sizes = imageLayout === "custom" ? props.sizes : SIZES_MAP[imageLayout];
  const loader = createCloudFrontLoader(
    props.height ? Number(props.height) : undefined,
    maxWidth,
    skipCloudFrontParams,
    isBanner,
    objectFit,
  );

  // If height is not provided, use fill mode with appropriate styling
  if (!props.height) {
    const { width, style, ...rest } = props;
    return <Image src={src} sizes={sizes} loader={loader} fill style={{ objectFit, ...style }} {...rest} />;
  }

  return <Image src={src} sizes={sizes} loader={loader} {...props} />;
  // return <Image src={src} sizes={sizes} {...props} />;
};

export default OptimizedImage;
